import type {CSSProperties} from "react";
import type {ThemeConfig} from "antd";
import {generate} from "@ant-design/colors";
import {atiColors, atiTheme, selectionTokens, softTint} from "./atiTheme";

type Brand = {primaryColor?: string | null; secondaryColor?: string | null};
export const normalizePublicBrandColor = (value?: string | null): string => {
    const text = value?.trim() ?? "";
    const hex = text.startsWith("#") ? text : `#${text}`;
    return /^#[0-9a-f]{6}$/i.test(hex) ? hex : "";
};
const rgb = (hex: string) => [1, 3, 5].map(offset => parseInt(hex.slice(offset, offset + 2), 16));
export const contrastOnWhite = (hex: string): number => {
    const channels = rgb(hex).map(channel => {
        const value = channel / 255;
        return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
    });
    return 1.05 / (channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722 + 0.05);
};
// Keep the supplied hue, reducing brightness only when white/text contrast requires it.
const readable = (hex: string): string => {
    if (contrastOnWhite(hex) >= 4.5) return hex;
    const channels = rgb(hex);
    for (let percent = 99; percent >= 0; percent--) {
        const candidate = `#${channels.map(channel => Math.round(channel * percent / 100).toString(16).padStart(2, "0")).join("")}`;
        if (contrastOnWhite(candidate) >= 4.5) return candidate;
    }
    return "#000000";
};
const rgba = (hex: string, alpha: number) => `rgba(${rgb(hex).join(", ")}, ${alpha})`;

export const buildPublicRegistrationTheme = (brand?: Brand | null): {
    theme: ThemeConfig;
    style: CSSProperties & Record<string, string>;
} => {
    const primary = readable(normalizePublicBrandColor(brand?.primaryColor) || atiColors.blue);
    const secondary = readable(normalizePublicBrandColor(brand?.secondaryColor) || atiColors.orange);
    const palette = generate(primary);
    // Not palette[0]: antd's palette greys out dark, muted brand colours.
    const soft = softTint(primary);
    const hover = readable(palette[4]);
    const active = readable(palette[6]);
    return {
        theme: {
            inherit: false,
            token: {
                ...atiTheme.token,
                colorPrimary: primary,
                colorPrimaryHover: hover,
                colorPrimaryActive: active,
                colorLink: primary,
                colorLinkHover: hover,
                colorLinkActive: active,
                colorInfo: primary,
                colorInfoBg: soft,
                ...selectionTokens(primary, soft),
                colorFillAlter: soft,
                colorInfoBorder: rgba(primary, 0.22)
            },
            components: {
                Button: {
                    fontWeight: 700,
                    primaryShadow: "none",
                    defaultColor: secondary,
                    defaultBorderColor: secondary,
                    defaultHoverColor: atiColors.white,
                    defaultHoverBg: secondary,
                    defaultHoverBorderColor: secondary,
                    defaultActiveColor: atiColors.white,
                    defaultActiveBg: readable(generate(secondary)[6]),
                    defaultActiveBorderColor: readable(generate(secondary)[6])
                },
                Form: atiTheme.components?.Form,
                Select: {optionSelectedColor: primary},
                Tabs: {cardBg: atiColors.soft, itemColor: atiColors.muted, itemActiveColor: active, itemHoverColor: hover,
                    itemSelectedColor: primary, inkBarColor: secondary},
                Segmented: {itemSelectedColor: primary, itemSelectedBg: atiColors.white, trackBg: soft}
            }
        },
        style: {
            "--ati-blue": primary,
            "--ati-blue-dark": active,
            "--ati-blue-line": rgba(primary, 0.22),
            "--ati-blue-soft": soft,
            "--ati-orange": secondary,
            "--ati-orange-line": rgba(secondary, 0.24),
            "--ati-orange-soft": softTint(secondary),
            "--ati-orange-text": secondary
        }
    };
};
